<template>
  <div
    class="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 relative overflow-hidden mb-4"
    :class="{ 'opacity-60 grayscale-[0.5]': maternalRSV?.vaccinated === true && !showForm }"
  >
    <!-- 狀態色條 -->
    <div
      class="absolute left-0 top-0 bottom-0 w-1.5"
      :class="{
        'bg-green-400': maternalRSV?.vaccinated === true,
        'bg-secondary': maternalRSV?.vaccinated === false || maternalRSV == null,
      }"
    ></div>

    <div>
      <!-- 標題列 -->
      <div class="flex justify-between items-start gap-2 mb-2">
        <div class="min-w-0">
          <h3 class="font-bold text-textMain text-lg">孕期 RSV 疫苗（媽媽）</h3>
          <div class="text-xs bg-gray-100 inline-block px-2 py-0.5 rounded-md mt-1">
            Abrysvo · 懷孕 24～36 週
          </div>
        </div>
        <div class="flex items-center gap-0.5 flex-shrink-0">
          <VaccineTypeBadge :type="getVaccineDetails('maternal_rsv')?.type" />
          <button
            @click="$router.push('/vaccine/maternal_rsv')"
            class="text-teal-500 text-xl w-8 h-8 flex items-center justify-center"
            aria-label="疫苗資訊"
          >
            ⓘ
          </button>
        </div>
      </div>

      <!-- 未設定：詢問是否接種 -->
      <div v-if="maternalRSV == null && !showForm" class="mt-3 border-t pt-3">
        <p class="text-sm text-textMuted mb-3">媽媽是否已接種孕期 RSV 疫苗？</p>
        <div class="flex gap-2">
          <button
            @click="setNotVaccinated"
            class="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-textMuted active:scale-95 transition-transform"
          >
            未接種
          </button>
          <button
            @click="openForm(true)"
            class="flex-1 py-2 bg-secondary rounded-xl text-xs font-bold active:scale-95 transition-transform"
          >
            確認接種
          </button>
        </div>
      </div>

      <!-- 已接種狀態 -->
      <div v-else-if="maternalRSV?.vaccinated && !showForm" class="mt-4 border-t pt-3 flex justify-between items-center">
        <div>
          <p class="text-xs text-textMuted">施打日期</p>
          <p class="font-bold font-mono text-sm">{{ maternalRSV.date || '未填寫' }}</p>
          <span v-if="maternalRSV.brand" class="text-xs bg-gray-100 px-2 py-0.5 rounded-md mt-1 inline-block">
            {{ maternalRSV.brand }}
          </span>
        </div>
        <button
          @click="openEdit"
          class="text-green-600 text-sm font-bold flex-shrink-0 whitespace-nowrap cursor-pointer hover:underline active:scale-95 transition-transform"
        >
          ✓ 已完成
        </button>
      </div>

      <!-- 未接種狀態 -->
      <div v-else-if="maternalRSV?.vaccinated === false && !showForm" class="mt-4 border-t pt-3 flex justify-between items-center">
        <p class="text-sm text-textMuted">媽媽未接種孕期 RSV 疫苗</p>
        <button
          @click="openForm(true)"
          class="text-textMuted text-sm cursor-pointer hover:underline active:scale-95 transition-transform"
        >
          改為已接種
        </button>
      </div>

      <!-- 填寫表單（新增 / 編輯） -->
      <div v-if="showForm" class="mt-3 border-t pt-3 space-y-3">
        <div>
          <label class="text-xs text-textMuted">施打日期（選填）</label>
          <div class="mt-1 border border-gray-200 rounded-xl px-3 py-2 overflow-hidden">
            <input
              type="date"
              v-model="formDate"
              class="w-full text-sm bg-transparent outline-none"
            />
          </div>
        </div>
        <div class="flex gap-2 items-center">
          <button
            v-if="isEditingExisting"
            @click="requestUnvaccinate"
            class="px-4 py-2 text-sm font-bold text-white bg-red-500 rounded-xl active:scale-95 transition-transform hover:bg-red-600"
          >
            改回未接種
          </button>
          <div class="flex gap-2 ml-auto">
            <button @click="cancelForm" class="px-4 py-2 text-sm text-textMuted border border-gray-200 rounded-xl active:scale-95 transition-transform">
              取消
            </button>
            <button @click="saveForm" class="px-4 py-2 text-sm font-bold bg-secondary rounded-xl active:scale-95 transition-transform">
              確定
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 改回未接種二次確認 -->
    <ConfirmDialog
      :show="showUnvaccinateConfirm"
      title="改回未接種"
      message="確定要將媽媽的孕期 RSV 疫苗改回未接種嗎？"
      confirm-text="改回未接種"
      danger
      @confirm="confirmUnvaccinate"
      @cancel="showUnvaccinateConfirm = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAppStore } from '../stores/useAppStore'
import ConfirmDialog from './ConfirmDialog.vue'
import VaccineTypeBadge from './VaccineTypeBadge.vue'
import { getVaccineDetails } from '../services/scheduleService'
import type { MaternalRSVVaccination } from '../types'

const RSV_BRAND = 'Abrysvo (Pfizer)'

const props = defineProps<{
  babyId: string
}>()

const store = useAppStore()

const maternalRSV = computed<MaternalRSVVaccination | null | undefined>(() => {
  const baby = store.babies.find(b => b.id === props.babyId)
  return baby?.maternalRSV
})

const showForm = ref(false)
const formDate = ref('')
// 是否為「編輯已接種紀錄」情境（openEdit 進入）。獨立於 maternalRSV.vaccinated，
// 避免 saveForm 送出後、showForm 尚未關閉前，reactive 狀態已先變成 true 造成按鈕短暫閃現。
const isEditingExisting = ref(false)

const openForm = (vaccinated: boolean) => {
  if (vaccinated) {
    isEditingExisting.value = false
    formDate.value = maternalRSV.value?.date ?? ''
    showForm.value = true
  }
}

const openEdit = () => {
  isEditingExisting.value = true
  formDate.value = maternalRSV.value?.date ?? ''
  showForm.value = true
}

const cancelForm = () => {
  showForm.value = false
  isEditingExisting.value = false
}

const setNotVaccinated = async () => {
  await store.updateMaternalRSV(props.babyId, { vaccinated: false })
}

const showUnvaccinateConfirm = ref(false)

const requestUnvaccinate = () => {
  showUnvaccinateConfirm.value = true
}

const confirmUnvaccinate = async () => {
  await store.updateMaternalRSV(props.babyId, { vaccinated: false })
  showForm.value = false
  isEditingExisting.value = false
  showUnvaccinateConfirm.value = false
}

const saveForm = async () => {
  const data: MaternalRSVVaccination = {
    vaccinated: true,
    ...(formDate.value && { date: formDate.value }),
    brand: RSV_BRAND,
  }
  await store.updateMaternalRSV(props.babyId, data)
  showForm.value = false
  isEditingExisting.value = false
}
</script>
